import React from 'react';
import { styled } from '@mui/material/styles';
import { List, ListItem, Typography, Collapse, Tooltip } from '@mui/material';
import { StageMessage } from '../types';
import { StageIcon } from './StageIcon';

const StyledList = styled(List)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  margin: 0,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.25, 0),
  display: 'flex',
  flexDirection: 'column',
  '&:first-of-type': {
    paddingTop: 0,
  },
  '&:last-of-type': {
    paddingBottom: theme.spacing(1),
  },
}));

const MessageRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

const MessageContent = styled('div')(() => ({
  flex: 1,
  minWidth: 0,
}));

const MessageTimestamp = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  marginLeft: theme.spacing(1),
  flexShrink: 0,
}));

interface MessageListProps {
  messages: StageMessage[];
  isExpanded: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, isExpanded }) => {
  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getMessageStatus = (type: StageMessage['type']): 'error' | 'error_continuing' | 'completed' => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'error_continuing';
      default:
        return 'completed';
    }
  };

  return (
    <Collapse in={isExpanded}>
      <StyledList>
        {messages.map((message) => (
          <StyledListItem key={message.id}>
            <MessageRow>
              <StageIcon status={getMessageStatus(message.type)} size="small" />
              <MessageContent>
                <Tooltip title={message.content} enterDelay={500}>
                  <Typography variant="body2" component="div" noWrap>
                    {message.content}
                  </Typography>
                </Tooltip>
              </MessageContent>
              <MessageTimestamp>
                {formatTimestamp(message.timestamp)}
              </MessageTimestamp>
            </MessageRow>
          </StyledListItem>
        ))}
      </StyledList>
    </Collapse>
  );
}; 